// Shared 2D curl-noise flow field for correlated sprite drift. Pure math + simplex-noise.
// NO PixiJS / browser API. All randomness via mulberry32 from '@shared/rng'.

import { createNoise3D, type NoiseFunction3D } from 'simplex-noise';
import { mulberry32, FIXED_SEED } from '@shared/rng';

/** Noise-space coordinate scale per pixel (≈1/600px feature size). */
export const SPACE_SCALE = 0.0016;
/** Finite-difference epsilon for the curl, in noise-space units. */
export const CURL_EPS = 0.5;
/** Per-sprite spring-back time constant (seconds). */
export const DRIFT_SPRING_TAU = 8.0;

/** z-evolution frequency in Hz as a function of the `space` axis. */
export function evolveHz(space: number): number {
  return 0.03 + 0.05 * space;
}

/** Flow amplitude in pixels as a function of `space`. */
export function ampPx(space: number): number {
  return 14 * (0.6 + 0.8 * space);
}

export interface FlowSample {
  vx: number; // curl x, roughly [-1,1]
  vy: number; // curl y, roughly [-1,1]
}

export interface FlowFieldOpts {
  seed?: number;
  reducedMotion?: boolean;
}

/**
 * Curl-noise flow field. Holds a simplex noise instance seeded deterministically and
 * an internal z/time coordinate that evolves with `space`. The 2D curl of a scalar
 * potential is divergence-free by construction (tested |div|<1e-3).
 */
export class FlowField {
  private readonly noise: NoiseFunction3D;
  private z = 0;
  reducedMotion: boolean;

  constructor(opts: FlowFieldOpts = {}) {
    const seed = opts.seed ?? FIXED_SEED;
    this.noise = createNoise3D(mulberry32(seed >>> 0));
    this.reducedMotion = opts.reducedMotion ?? false;
  }

  /** Current internal time/z coordinate (noise-space). Exposed for tests. */
  get time(): number {
    return this.z;
  }

  /** Advance the field's time coordinate. Frozen under reduced motion. */
  evolve(dtSec: number, space: number): void {
    if (this.reducedMotion) return;
    this.z += dtSec * 2 * Math.PI * evolveHz(space);
  }

  /**
   * Sample the curl of the noise field at pixel (x,y) using the current z.
   * 2D curl from scalar potential n:
   *   vx =  (n(x, y+e) - n(x, y-e)) / (2e)
   *   vy = -(n(x+e, y) - n(x-e, y)) / (2e)
   * Returns zero field under reduced motion.
   */
  sample(x: number, y: number): FlowSample {
    if (this.reducedMotion) return { vx: 0, vy: 0 };
    return this.curlAt(x * SPACE_SCALE, y * SPACE_SCALE, this.z);
  }

  /** Sample at an explicit z (does not touch internal state). Used in tests. */
  sampleAt(x: number, y: number, z: number): FlowSample {
    if (this.reducedMotion) return { vx: 0, vy: 0 };
    return this.curlAt(x * SPACE_SCALE, y * SPACE_SCALE, z);
  }

  private curlAt(nx: number, ny: number, z: number): FlowSample {
    const e = CURL_EPS;
    const n = this.noise;
    const vx = (n(nx, ny + e, z) - n(nx, ny - e, z)) / (2 * e);
    const vy = -(n(nx + e, ny, z) - n(nx - e, ny, z)) / (2 * e);
    return { vx, vy };
  }
}

/**
 * Convenience: produce a `flowAt(x,y,t,space)` closure with divergence ~0.
 * Builds a field, samples at z derived from t and space. Useful for stateless tests.
 */
export function makeFlowAt(opts: FlowFieldOpts = {}): (
  x: number,
  y: number,
  t: number,
  space: number,
) => FlowSample {
  const field = new FlowField(opts);
  return (x, y, t, space) => {
    if (field.reducedMotion) return { vx: 0, vy: 0 };
    const z = t * 2 * Math.PI * evolveHz(space);
    return field.sampleAt(x, y, z);
  };
}

/** Per-sprite breathing scale factor: 1 + 0.03*sin(2π·hz·t + phase). 1 under reduced motion. */
export function breath(t: number, hz: number, phase: number, reducedMotion = false): number {
  if (reducedMotion) return 1;
  return 1 + 0.03 * Math.sin(2 * Math.PI * hz * t + phase);
}

/**
 * Integrate one sprite's drift toward the flow with an exponential spring back home.
 * driftNext = (drift + v*ampPx*dt) * exp(-dt/tau).  Returns updated drift.
 */
export function integrateDrift(
  drift: number,
  v: number,
  amp: number,
  dtSec: number,
): number {
  const pushed = drift + v * amp * dtSec;
  return pushed * Math.exp(-dtSec / DRIFT_SPRING_TAU);
}
